import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { mediaUrl } from "../api";

const SITE_NAME = "Fleetroot";
const DEFAULT_DESC = "Fleetroot helps businesses connect their vehicles, drivers, devices and transport operations to achieve real-time fleet visibility, optimized routing, efficient dispatch, and digital proof of delivery, all in one place.";
const DEFAULT_IMAGE = "/og-default.png";

export const origin = () => (typeof window !== "undefined" ? window.location.origin : "");

const abs = (u) => {
  if (!u) return "";
  const m = mediaUrl(u);
  return m.startsWith("http") ? m : `${origin()}${m}`;
};

export const Seo = ({ title, description = DEFAULT_DESC, image, type = "website", noindex = false, jsonLd, canonicalPath, markdownUrl }) => {
  const { pathname } = useLocation();
  const fullTitle = title ? (title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`) : `${SITE_NAME} — Fleet management & last-mile delivery, simplified.`;
  const canonical = `${origin()}${canonicalPath || pathname}`.replace(/\/$/, "") || origin();
  const img = abs(image || DEFAULT_IMAGE);
  const ld = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {markdownUrl && <link rel="alternate" type="text/markdown" href={markdownUrl} title="Markdown version" />}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={img} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={img} />
      {ld.map((obj, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
      ))}
    </Helmet>
  );
};

export const organizationLd = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: origin(),
  logo: abs(DEFAULT_IMAGE),
  sameAs: ["https://www.facebook.com/fleetroot", "https://www.linkedin.com/company/30948197/", "https://instagram.com/fleetroot.inc", "https://www.youtube.com/channel/UCjW0ow7iEsu_r6sdLQnHe3w/featured"],
  contactPoint: [{ "@type": "ContactPoint", email: "sales@fleetroot.com", telephone: "+971525234328", contactType: "sales", areaServed: "AE" }],
});

export const faqLd = (faq) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
});

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: `${origin()}${it.path}` })),
});

export const articleLd = (post) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: post.title,
  description: post.seoDescription || post.excerpt,
  image: abs(post.ogImage || post.cover),
  datePublished: post.date,
  dateModified: (post.updatedAt || post.date || "").slice(0, 10),
  author: { "@type": "Organization", name: post.author || SITE_NAME },
  publisher: { "@type": "Organization", name: SITE_NAME, logo: { "@type": "ImageObject", url: abs(DEFAULT_IMAGE) } },
  mainEntityOfPage: `${origin()}/blog/${post.slug}`,
});

export default Seo;
